import React, { Fragment } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import TextInput from '@Components/TextInput'
import Spinner from '@Components/Spinner'
import ButtonLink from '@Components/Button'
import { sharedMessages as commonMessages } from '@Locales/index'
import messages from '@App/session/messages'
import { SignInFormButton, Warning } from '../index'
import { presenterHub } from '@RestApi/types'
import RequestResetPasswordSentDialog from './RequestResetPasswordSentDialog'
import { HubFormattedMessage } from 'src/common/rtl/HubTextFormatting'

type props = {
  onCloseResetPassword: () => void
  hub: presenterHub
  action: string
  loading: boolean
  isAuthorized: boolean
  badEmailError: boolean
  emptyEmailError: boolean
  emailAddress: string
  handleSetEmailAddress: (e: React.ChangeEvent<HTMLInputElement>) => void
  resetAction: () => void
  handleEmailSubmit: () => void
  handleRequesResetPassword: () => Promise<void>
  validateEmailAddress: () => boolean
}

const RequestResetPassword: React.FC<props> = (props: props) => {
  const intl = useIntl()
  const {
    hub,
    onCloseResetPassword,
    handleSetEmailAddress,
    validateEmailAddress,
    badEmailError,
    emptyEmailError,
    emailAddress,
    action,
    handleRequesResetPassword,
    loading,
  } = props

  const handleSubmit = () => {
    if (validateEmailAddress()) {
      handleRequesResetPassword()
    }
  }

  const passwordResetDescription = () => {
    const { uid, name } = hub

    const description = uid ? (
      <HubFormattedMessage
        {...messages.orgResetPasswordDescription}
        values={{
          orgName: <strong>{name}</strong>,
        }}
      />
    ) : (
      <HubFormattedMessage
        {...messages.hubResetPasswordDescription}
        values={{
          hubName: <strong>{uid}</strong>,
        }}
      />
    )
    return description
  }

  return (
    <Fragment>
      {action === 'requestResetPassword-success' ? (
        <RequestResetPasswordSentDialog
          onCloseResetPassword={onCloseResetPassword}
          emailAddress={emailAddress}
        />
      ) : loading ? (
        <Spinner />
      ) : (
        <Container>
          <h1>
            <HubFormattedMessage {...messages.magicLinkHeader} />
            <br />
          </h1>
          {emptyEmailError ? (
            <Warning>
              <HubFormattedMessage {...commonMessages.emptyEmailError} />
            </Warning>
          ) : badEmailError ? (
            <Warning>
              <HubFormattedMessage {...commonMessages.badEmailError} />
            </Warning>
          ) : (
            <ResetPasswordDescription>{passwordResetDescription()}</ResetPasswordDescription>
          )}
          <StyledTextInput
            aria-label={intl ? intl.formatMessage(commonMessages.email) : ''}
            placeholder={intl ? intl.formatMessage(commonMessages.email) : ''}
            name="email"
            type="email"
            value={emailAddress}
            autoFocus={!emailAddress || emailAddress.length === 0}
            required
            onChange={handleSetEmailAddress}
          />
          <SignInFormButton displayType="primary" onClick={handleSubmit}>
            <HubFormattedMessage {...commonMessages.continue} />
          </SignInFormButton>
          <BackLinkContainer>
            <BackLink onClick={onCloseResetPassword}>
              <HubFormattedMessage {...messages.closeResetPassword} />
            </BackLink>
          </BackLinkContainer>
        </Container>
      )}
    </Fragment>
  )
}
export default RequestResetPassword
const Container = styled.div`
  max-width: 300px;
`

const StyledTextInput = styled(TextInput)`
  margin-top: 35px;
  height: 50px;
  width: 300px;
  margin: 0;
`

export const ResetPasswordDescription = styled.p`
  color: black;
  margin-top: 25px;
  margin-bottom: 35px;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeNormal};
  max-width: 300px;
`

const BackLinkContainer = styled.div`
  text-align: center;
`

export const BackLink = styled(ButtonLink)`
  color: ${({ theme }) => theme.colors.linkedin};
  margin-left: 10px;
  background-color: white;
  border: transparent;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmaller};
  &:hover,
  &:focus {
    text-decoration: underline;
    background-color: white;
  }
`
